"use client"

import { useState, useCallback, useEffect, useRef } from "react"

/**
 * Manages a closing animation delay before actually calling onClose.
 * Returns `closing` (true during exit animation) and `requestClose` (triggers the sequence).
 * Also handles Escape key to dismiss.
 */
export function useAnimatedClose(onClose: () => void, duration = 200) {
  const [closing, setClosing] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const requestClose = useCallback(() => {
    setClosing((prev) => {
      if (prev) return prev
      timerRef.current = setTimeout(() => onCloseRef.current(), duration)
      return true
    })
  }, [duration])

  /** Reset closing state so the hook can be reused after the modal reopens. */
  const reset = useCallback(() => {
    clearTimeout(timerRef.current)
    setClosing(false)
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [requestClose])

  useEffect(() => () => clearTimeout(timerRef.current), [])

  return { closing, requestClose, reset }
}
